import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ConnectDotsBackground from '../components/ui/ConnectDotsBackground';
import SectionHeading from '../components/ui/SectionHeading';

const WorkWithMe = () => {
  useEffect(() => {
    document.title = "Work With Me - Fractional CMO & MarTech Consulting Services | Julie Adams";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fractional CMO and martech consulting services for B2B companies $10M-$100M+. From DIY resources to full executive leadership. Book a free consultation.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Fractional CMO and martech consulting services for B2B companies $10M-$100M+. From DIY resources to full executive leadership. Book a free consultation.';
      document.head.appendChild(meta);
    }

    // AEO/GEO Schema Markup
    let script = document.querySelector('#work-with-me-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'work-with-me-schema';
      script.type = 'application/ld+json';
      const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Fractional CMO & MarTech Consulting",
        "provider": {
          "@type": "Person",
          "name": "Julie Adams",
          "jobTitle": "Marketing Technology Orchestrator"
        },
        "description": "Strategic marketing operations and executive leadership tailored for B2B companies.",
        "offers": [
          {
            "@type": "Offer",
            "name": "Executive Advisory",
            "priceCurrency": "USD",
            "price": "1500",
            "description": "For capable teams who need expert validation."
          },
          {
            "@type": "Offer",
            "name": "Guided Sprint",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "15000",
              "maxPrice": "25000",
              "priceCurrency": "USD"
            },
            "description": "6-week transformation for companies ready to scale."
          },
          {
            "@type": "Offer",
            "name": "Strategic Operations Support (SOS)",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "5000",
              "maxPrice": "7500",
              "priceCurrency": "USD"
            },
            "description": "Your fractional strategist, on-demand."
          },
          {
            "@type": "Offer",
            "name": "Fractional CMO",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "minPrice": "10000",
              "maxPrice": "20000",
              "priceCurrency": "USD"
            },
            "description": "Full CMO-level strategic leadership."
          }
        ]
      };
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }

    return () => {
      const existingScript = document.querySelector('#work-with-me-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const calendarLink = "https://calendar.app.google/zzGx7bhDRmwKPJm57";

  return (
    <div className="relative min-h-screen bg-white dark:bg-brand-darker text-brand-dark dark:text-white pt-24 overflow-hidden">
      <div className="relative z-10 space-y-32 pb-32">

        {/* HERO SECTION */}
        <section className="relative pt-16 md:pt-24 text-center w-full overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
            }}
          ></div>
          <div className="absolute inset-0 z-[-1] pointer-events-none">
            <ConnectDotsBackground />
          </div>

          <div className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-8 leading-tight text-brand-dark dark:text-white">
              Marketing Leadership Built for <span className="text-brand-yellow">How You Actually Work</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-700 dark:text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              From one-time strategy sessions to full fractional CMO leadership—get the exact level of strategic support your business needs, when you need it.
            </p>

            <div className="flex flex-col items-center">
              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white px-8 py-4 rounded-md text-lg font-bold hover:scale-105 hover:shadow-[0_0_15px_rgba(64,224,208,0.5)] transition-all duration-300 w-full md:w-auto"
              >
                <SafeIcon name="Calendar" className="w-5 h-5 shrink-0" /> Book Free Consultation
              </a>
            </div>
          </div>
        </section>

        {/* SERVICE TIERS SECTION */}
        <section className="relative w-full overflow-hidden py-16 md:py-24">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
            }}
          ></div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <SectionHeading title="Choose Your Level of Support" align="center" />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 relative">

            {/* Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-full hover:border-brand-cyan/50 transition-colors"
            >
              <div className="text-4xl mb-4">💡</div>
              <h3 className="text-2xl font-bold mb-2">Executive Advisory & DIY Resources</h3>
              <p className="text-brand-cyan font-semibold mb-4">For capable teams who need expert validation</p>

              <div className="bg-white dark:bg-brand-darker p-4 rounded-lg mb-6 border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Investment</p>
                <p className="text-lg font-bold text-brand-yellow">$500-$2,500</p>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-slate-500">Best For:</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300">Marketing leaders with strong teams, one-time strategic challenges, budget-conscious startups</p>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-8 flex-grow">
                Get immediate access to the frameworks I use with Fortune 500 clients, or book a 90-minute Executive Advisory Session to solve your most pressing marketing challenge.
              </p>

              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-red/10 text-brand-red dark:text-brand-cyan dark:bg-brand-cyan/10 hover:bg-brand-red hover:text-white dark:hover:bg-brand-cyan dark:hover:text-brand-dark border border-brand-red/20 dark:border-brand-cyan/20 px-4 sm:px-6 py-3 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              >
                <SafeIcon name="Calendar" className="w-4 h-4 shrink-0" /> Book Advisory Session
              </a>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-full hover:border-brand-cyan/50 transition-colors"
            >
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold mb-2">Guided Sprint — Strategic Marketing Intensive</h3>
              <p className="text-brand-cyan font-semibold mb-4">6-week transformation for companies ready to scale</p>

              <div className="bg-white dark:bg-brand-darker p-4 rounded-lg mb-6 border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Investment</p>
                <p className="text-lg font-bold text-brand-yellow">$15,000-$25,000</p>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-slate-500">Best For:</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300">Product launches, market entry, strategic resets, companies $5M-$50M</p>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Complete marketing operations audit, strategic roadmap, martech optimization, and everything your team needs to execute with confidence—delivered in 6 focused weeks.
              </p>

              <div className="mb-8 flex-grow">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">What's Included:</h4>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Full marketing operations audit</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Strategic roadmap (6-12 months)</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> MarTech stack optimization recommendations</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Go-to-market strategy development</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Weekly strategy calls (6 sessions)</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Final presentation + executive report</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> 30-day post-Sprint email support</li>
                </ul>
              </div>

              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-red/10 text-brand-red dark:text-brand-cyan dark:bg-brand-cyan/10 hover:bg-brand-red hover:text-white dark:hover:bg-brand-cyan dark:hover:text-brand-dark border border-brand-red/20 dark:border-brand-cyan/20 px-4 sm:px-6 py-3 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              >
                <SafeIcon name="Calendar" className="w-4 h-4 shrink-0" /> Apply for Sprint
              </a>
            </motion.div>

            {/* Card 3 - HIGHLIGHTED */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2 bg-slate-900 text-white p-8 md:p-12 rounded-2xl border-2 border-brand-cyan relative overflow-hidden shadow-[0_0_30px_rgba(64,224,208,0.15)] flex flex-col h-full transform md:scale-[1.02]"
            >
              <div className="hidden md:block absolute top-0 right-0 w-64 h-64 bg-brand-cyan/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

              <div className="relative z-10 flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-4xl">🧭</div>
                    <span className="bg-brand-red text-white text-xs uppercase font-bold tracking-widest px-3 py-1 rounded-full">NEW</span>
                  </div>
                  <h3 className="text-3xl font-bold mb-2">SOS — Strategic Operations Support</h3>
                  <p className="text-brand-cyan font-semibold text-lg mb-6">Your fractional strategist, on-demand</p>

                  <div className="bg-brand-darker/50 p-4 rounded-lg mb-6 border border-white/10 inline-block">
                    <p className="text-sm text-slate-400 mb-1">Investment</p>
                    <p className="text-xl font-bold text-brand-yellow">$5,000-$7,500/month <span className="text-sm font-normal text-slate-400">(3-month min)</span></p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-slate-400">Best For:</h4>
                    <p className="text-sm text-slate-300">Companies with internal marketing teams who need strategic oversight, CMOs who need a strategic partner, post-Sprint clients</p>
                  </div>

                  <p className="text-slate-300 mb-8 text-lg leading-relaxed">
                    Perfect if you have a capable marketing team but need an experienced strategist in your corner. You get strategic direction, operations oversight, and expert guidance without paying for a full fractional CMO. Think of it as having a strategic advisor who knows martech inside and out, available on-demand to keep you on track. You're not paying for execution—you're paying for the strategic brain that ensures your team is working on the right things.
                  </p>

                  <a
                    href={calendarLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-brand-red text-white px-8 py-4 rounded text-sm font-bold uppercase tracking-widest hover:scale-105 hover:shadow-[0_0_15px_rgba(255,42,42,0.5)] transition-all duration-300"
                  >
                    <SafeIcon name="Calendar" className="w-4 h-4 shrink-0" /> Schedule SOS Discovery Call
                  </a>
                </div>

                <div className="flex-1 bg-brand-darker/40 p-6 rounded-xl border border-white/5 backdrop-blur-sm">
                  <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-brand-yellow">What's Included:</h4>
                  <ul className="space-y-4 text-slate-300">
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> 10-15 hours/month strategic advisory</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Bi-weekly strategy calls (60 min)</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Marketing operations audit + recommendations</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Strategic roadmap development</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Vendor/tool evaluation guidance</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Executive reporting + dashboards</li>
                    <li className="flex gap-3 items-start"><SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" /> Email/Slack access for strategic questions</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Card 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-full hover:border-brand-cyan/50 transition-colors"
            >
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-2xl font-bold mb-2">Fractional CMO — Full Strategic Leadership</h3>
              <p className="text-brand-cyan font-semibold mb-4">Executive marketing leadership without the executive salary</p>

              <div className="bg-white dark:bg-brand-darker p-4 rounded-lg mb-6 border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Investment</p>
                <p className="text-lg font-bold text-brand-yellow">$10,000-$20,000/month <span className="text-sm font-normal text-slate-500 dark:text-slate-400">(6-month min)</span></p>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-slate-500">Best For:</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300">Companies $20M-$100M+, PE-backed organizations, companies in transformation</p>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-6">
                I become your marketing leader—managing your team, owning your strategy, driving execution, and reporting to your executive team. Full CMO-level leadership, fractional commitment.
              </p>

              <div className="mb-8 flex-grow">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">What's Included:</h4>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Full CMO-level strategic leadership</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Direct management of marketing team/agency partners</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Complete marketing operations ownership</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Budget management & ROI optimization</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Executive team collaboration & reporting</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Strategic planning & execution oversight</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> MarTech stack management & vendor relationships</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Board-level reporting & presentations</li>
                  <li className="flex gap-2 items-start"><SafeIcon name="Check" className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" /> Unlimited strategic access</li>
                </ul>
              </div>

              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-red/10 text-brand-red dark:text-brand-cyan dark:bg-brand-cyan/10 hover:bg-brand-red hover:text-white dark:hover:bg-brand-cyan dark:hover:text-brand-dark border border-brand-red/20 dark:border-brand-cyan/20 px-4 sm:px-6 py-3 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              >
                <SafeIcon name="Calendar" className="w-4 h-4 shrink-0" /> Apply for Fractional CMO
              </a>
            </motion.div>

            {/* Card 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col h-full hover:border-brand-cyan/50 transition-colors"
            >
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-2">Project-Based Engagements</h3>
              <p className="text-brand-cyan font-semibold mb-4">Targeted solutions for specific challenges</p>

              <div className="bg-white dark:bg-brand-darker p-4 rounded-lg mb-6 border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Investment</p>
                <p className="text-lg font-bold text-brand-yellow">$8,500-$45,000 <span className="text-sm font-normal text-slate-500 dark:text-slate-400">(varies)</span></p>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Need a specific project completed? I offer focused engagements for martech audits, consolidation projects, LEAN implementation, and emergency CMO transitions.
              </p>

              <div className="mb-8 flex-grow">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 text-slate-500">Project Types:</h4>
                <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                  <li className="flex flex-col gap-1">
                    <span className="font-semibold text-brand-dark dark:text-white">Tool Trap™ Audit (Full Service)</span>
                    <span className="text-brand-yellow">$8,500-$12,000</span>
                  </li>
                  <li className="flex flex-col gap-1">
                    <span className="font-semibold text-brand-dark dark:text-white">Martech Consolidation Project</span>
                    <span className="text-brand-yellow">$25,000-$45,000</span>
                  </li>
                  <li className="flex flex-col gap-1">
                    <span className="font-semibold text-brand-dark dark:text-white">LEAN Marketing Implementation</span>
                    <span className="text-brand-yellow">$18,000-$22,000</span>
                  </li>
                  <li className="flex flex-col gap-1">
                    <span className="font-semibold text-brand-dark dark:text-white">Emergency CMO Transition Support</span>
                    <span className="text-brand-yellow">$15,000</span>
                  </li>
                </ul>
              </div>

              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-red/10 text-brand-red dark:text-brand-cyan dark:bg-brand-cyan/10 hover:bg-brand-red hover:text-white dark:hover:bg-brand-cyan dark:hover:text-brand-dark border border-brand-red/20 dark:border-brand-cyan/20 px-4 sm:px-6 py-3 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              >
                <SafeIcon name="Calendar" className="w-4 h-4 shrink-0" /> Discuss Your Project
              </a>
            </motion.div>
          </div>
          </div>
        </section>

        {/* DECISION TREE SECTION */}
        <section className="relative w-full overflow-hidden py-16 md:py-24 bg-slate-50 dark:bg-brand-dark/50 rounded-3xl mt-16 max-w-4xl mx-auto">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
            }}
          ></div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-black mb-4">Not Sure Where to Start?</h2>
              <p className="text-xl text-brand-cyan font-semibold">Most Common Paths</p>
            </div>

            <ul className="space-y-6 mb-12 text-lg">
              <li className="flex items-start gap-4">
                <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-yellow shrink-0 mt-1" />
                <div>
                  <span className="font-bold text-brand-dark dark:text-white">You have a specific challenge:</span> Start with an <strong>Executive Advisory Session</strong> ($1,500)
                </div>
              </li>
              <li className="flex items-start gap-4">
                <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-yellow shrink-0 mt-1" />
                <div>
                  <span className="font-bold text-brand-dark dark:text-white">You're launching something new or need a strategic reset:</span> <strong>Guided Sprint</strong> ($15K-$25K)
                </div>
              </li>
              <li className="flex items-start gap-4">
                <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-yellow shrink-0 mt-1" />
                <div>
                  <span className="font-bold text-brand-dark dark:text-white">You have a team but need ongoing strategic direction:</span> <strong>SOS</strong> ($5K-$7.5K/month)
                </div>
              </li>
              <li className="flex items-start gap-4">
                <SafeIcon name="CheckCircle" className="w-6 h-6 text-brand-yellow shrink-0 mt-1" />
                <div>
                  <span className="font-bold text-brand-dark dark:text-white">You need full marketing leadership:</span> <strong>Fractional CMO</strong> ($10K-$20K/month)
                </div>
              </li>
              <li className="flex items-start gap-4">
                <SafeIcon name="HelpCircle" className="w-6 h-6 text-brand-red shrink-0 mt-1" />
                <div>
                  <span className="font-bold text-brand-dark dark:text-white">Not sure?</span> Book a free 20-minute consultation and I'll help you figure out the right fit.
                </div>
              </li>
            </ul>

            <div className="text-center">
              <a
                href={calendarLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white px-8 py-4 rounded text-lg font-bold hover:scale-105 hover:shadow-[0_0_15px_rgba(255,42,42,0.5)] transition-all duration-300"
              >
                <SafeIcon name="Calendar" className="w-5 h-5 shrink-0" /> Book Free Consultation
              </a>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF SECTION */}
        <section className="relative w-full overflow-hidden py-16 md:py-24">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
            }}
          ></div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <SectionHeading title="From Strategy to Execution—However You Need It" align="center" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">

            <div className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
              <SafeIcon name="MessageSquare" className="absolute -bottom-4 -right-4 w-32 h-32 text-slate-200 dark:text-slate-800 opacity-50 group-hover:text-brand-cyan/10 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex text-brand-yellow mb-6">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} name="Star" className="w-5 h-5 fill-current" />)}
                </div>
                <p className="text-slate-700 dark:text-slate-300 italic mb-6 leading-relaxed">
                  "We hired Julie for a Guided Sprint to help us with a product launch. After 6 weeks, we realized we needed her ongoing—so we moved to SOS. Having her available for strategic questions while our team handles execution has been game-changing."
                </p>
                <p className="font-bold text-brand-dark dark:text-white">— Marketing Director, $30M SaaS Company</p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
              <SafeIcon name="MessageCircle" className="absolute -bottom-4 -right-4 w-32 h-32 text-slate-200 dark:text-slate-800 opacity-50 group-hover:text-brand-cyan/10 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex text-brand-yellow mb-6">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} name="Star" className="w-5 h-5 fill-current" />)}
                </div>
                <p className="text-slate-700 dark:text-slate-300 italic mb-6 leading-relaxed">
                  "SOS is the perfect middle ground. We have a strong internal team, but Julie's strategic oversight ensures we're not just busy—we're effective. It's like having a CMO on speed-dial."
                </p>
                <p className="font-bold text-brand-dark dark:text-white">— CEO, $45M B2B Services Company</p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-2xl relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
              <SafeIcon name="MessageSquare" className="absolute -bottom-4 -right-4 w-32 h-32 text-slate-200 dark:text-slate-800 opacity-50 group-hover:text-brand-cyan/10 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex text-brand-yellow mb-6">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} name="Star" className="w-5 h-5 fill-current" />)}
                </div>
                <p className="text-slate-700 dark:text-slate-300 italic mb-6 leading-relaxed">
                  "We saved $180K annually and gained 40% efficiency after working with Julie. Her Tool Trap™ methodology transformed how we approach martech. The audit alone paid for itself within 90 days."
                </p>
                <p className="font-bold text-brand-dark dark:text-white">— VP of Marketing, $30M B2B Manufacturing Company</p>
              </div>
            </div>

          </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="w-full relative overflow-hidden py-16 md:py-24">
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)',
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 30%, black 100%)'
            }}
          ></div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto">
            <SectionHeading title="Understanding SEVEN HATS® and The Tool Trap™" align="center" subtitle="FAQ" />

            <div className="mt-12 space-y-8">
              <div className="bg-slate-50 dark:bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-brand-dark dark:text-white flex gap-3 items-start">
                  <SafeIcon name="HelpCircle" className="w-6 h-6 text-brand-red shrink-0 mt-1" />
                  What is the SEVEN HATS® methodology?
                </h3>
                <div className="text-slate-700 dark:text-slate-300 space-y-4 ml-9">
                  <p>
                    SEVEN HATS® is actually a <strong>Strategic Operations Platform</strong>, not just a methodology. It's the command center where marketing strategy becomes operational reality.
                  </p>
                  <p>The platform operationalizes proven methodologies including:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>The Tool Trap™ framework</strong> - My proprietary audit process for martech consolidation</li>
                    <li><strong>LEAN Marketing principles</strong> - Waste elimination and process optimization</li>
                    <li><strong>Design Thinking</strong> - Inspired by Dr. Edward de Bono's Six Thinking Hats</li>
                  </ul>
                  <p>
                    <strong>The difference?</strong> Most consultants give you a strategy deck. I give you SEVEN HATS®—the system where that strategy becomes executable workflows, dashboards, and measurable results.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-brand-dark dark:text-white flex gap-3 items-start">
                  <SafeIcon name="HelpCircle" className="w-6 h-6 text-brand-red shrink-0 mt-1" />
                  What is The Tool Trap™?
                </h3>
                <div className="text-slate-700 dark:text-slate-300 space-y-4 ml-9">
                  <p>
                    The Tool Trap™ is my proprietary diagnostic framework for identifying and eliminating martech waste. It's a proven 5-phase methodology:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2 font-medium">
                    <li><strong>Inventory</strong> - What tools do you actually have?</li>
                    <li><strong>Usage Analysis</strong> - What are you actually using?</li>
                    <li><strong>Cost Analysis</strong> - What are you actually paying?</li>
                    <li><strong>Overlap Analysis</strong> - Where's the redundancy?</li>
                    <li><strong>Gap Analysis</strong> - What's actually missing?</li>
                  </ol>
                  <p>
                    Clients using The Tool Trap™ framework typically identify 30-45% cost savings within the first 90 days.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800">
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-brand-dark dark:text-white flex gap-3 items-start">
                  <SafeIcon name="HelpCircle" className="w-6 h-6 text-brand-red shrink-0 mt-1" />
                  How do SEVEN HATS® and The Tool Trap™ work together?
                </h3>
                <div className="text-slate-700 dark:text-slate-300 space-y-4 ml-9">
                  <p>
                    The Tool Trap™ is the diagnostic methodology—it identifies the problems in your martech stack. SEVEN HATS® is the operational platform—it's where we implement the solutions and ensure you don't recreate the chaos.
                  </p>
                  <p>Think of it this way:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>The Tool Trap™</strong> = The "what" (the audit process)</li>
                    <li><strong>SEVEN HATS®</strong> = The "how" (the execution system)</li>
                  </ul>
                  <p>
                    Together, they create a complete approach to building efficient, scalable MarkOps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="px-4 sm:px-6 lg:px-12 text-center max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black mb-6 text-brand-dark dark:text-white">Ready to Get Started?</h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-12">
            Book a free 20-minute consultation to figure out which option is right for you.
          </p>
          <a
            href={calendarLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-brand-red text-white px-10 py-5 rounded-md text-xl font-bold hover:scale-105 hover:shadow-[0_0_20px_rgba(255,42,42,0.6)] transition-all duration-300 w-full sm:w-auto"
          >
            <SafeIcon name="Calendar" className="w-6 h-6 shrink-0" /> Book Your Free Consultation
          </a>
        </section>

      </div>
    </div>
  );
};

export default WorkWithMe;
